import React, { useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Pending } from './helperComponents'
import DropdownActions from './DropdownActions'
import { postRequest } from '../../services/base'
import { NavContext } from '../../context/global_context'
import { UploadContext } from '../../context/upload_file_context'
import SelectInput from '../common/SelectInput'
import { Button, Flex } from 'antd'
import EllipsisTooltip from '../common/EllipsisTooltip'
import { prettyPrintFormData } from '../../helpers/utils'
import FileInput from './FileInput'
import dayjs from 'dayjs'

const FileActions = (props) => {
  const {
    formRef,
    filesList,
    fileType,
    fileName,
    is_uploading,
    reloadData,
    resetStations,
  } = props
  const [navValue, dispatch] = useContext(NavContext)
  const [uploadState, setUploadState] = useContext(UploadContext)
  const downloadUrl = ''
  const loadingFile = ''

  const importVersions = () => {
    const options = []
    if (fileType == 'sales_code_constraints') {
      options.push({
        value: 'current_state_inlcude_not_constrained',
        label: 'Current State (Include Not Constrained)',
      })
    }
    if (fileType == 'connector_layout') {
      options.push({
        value: 'current_state_include_part_number',
        label: 'Current State (Include Part Number)',
      })
    }
    options.push({
      value: 'all',
      label: 'Current State',
    })

    return [
      ...options,
      ...filesList
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((file) => ({
          value: file.id,
          // label: file.created_at,
          label: dayjs(file.created_at).format('DD-MM-YYYY hh:mm A'),

          id: file.id,
          importedBy: file.user.email,
          errors: file.error_reason,
          file: file,
        })),
    ]
  }
  const fileImportVersions = useMemo(() => {
    return importVersions()
  }, [filesList, fileType])

  const buildFormData = (file, fileType, attrs = {}) => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'File upload is in progress',
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()

    if (attrs?.svg_side) {
      formData.append('files', file, `${fileType}.zip`)
    } else {
      formData.append('files', file, `${fileType}.csv`)
    }

    formData.append('file_type', fileType)
    formData.append('vehicle_id', navValue?.id)

    Object.entries(attrs).forEach(([key, val]) => {
      formData.append(key, val)
    })

    return formData
  }

  const uploadFile = (file, attrs = {}) => {
    const formData = buildFormData(file, fileType, attrs)
    processUplaodedFile(formData)
  }

  const processUplaodedFile = async (data) => {
    let res = await postRequest(`/api/importer/upload/`, data)
    if (res?.error_code) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res?.msg,
          loader: true,
        },
      })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'File Successfully uploaded into system and we are processing it.',
          loader: true,
        },
      })
    }

    setUploadState({})
    reloadData()
  }

  const disabledImports = [
    'connectorlist',
    'devices',
    'category_connector',
    'componentalias',
  ]

  return (
    <>
      {is_uploading ? (
        <div className="ml-2" style={{ minWidth: '150px' }}>
          {is_uploading ? (
            <span className="uploader-loader"></span>
          ) : (
            <Pending />
          )}
        </div>
      ) : (
        <div className={`action-items-wrapper ${loadingFile && 'hidden'}`}>
          {fileType === 'connector_layout' ? (
            <>
              <FileInput
                labelTitle={'Import Connector Side'}
                fileFieldName={fileType}
                fileFieldId={'connector-layout-connector-side'}
                className='link-button import-connector'
                onChange={(file) =>
                  uploadFile(file, { svg_side: 'connector' })
                }
              />
              <FileInput
                labelTitle={'Import Terminal Side'}
                fileFieldName={fileType}
                fileFieldId={'connector-layout-terminal-side'}
                className='link-button import-terminal'
                onChange={(file) => uploadFile(file, { svg_side: 'terminal' })}
              />
            </>
          ) : fileType === 'stations' ? (
            <>
              <Button type='link' className='link-button danger' onClick={() => resetStations()}>Reset</Button>
              <FileInput
                labelTitle={'Import'}
                fileFieldName={fileType}
                fileFieldId={fileType}
                className='link-button'
                onChange={uploadFile}
                isDisabled={disabledImports.includes(fileType)}
              />
            </>
          ) : (
            <FileInput
              labelTitle={'Import'}
              fileFieldName={fileType}
              fileFieldId={fileType}
              className='link-button'
              onChange={uploadFile}
              isDisabled={disabledImports.includes(fileType)}
            />
          )}

          <SelectInput
            name={fileType}
            label="Select version"
            hideLabel={true}
            placeholder="Select version"
            isRequired={false}
            allowClear={false}
            wrapperClass="select-version"
            options={fileImportVersions}
            onChange={(option) => {
              setUploadState({
                type: 'version',
                data: option,
                file_type: fileType,
                file_name: fileName,
              })
            }}
            formatOption={(option) => {
              const id = option?.id ? `id: ${option.id}` : ''
              const date = option.label
              const email = option?.importedBy ? `${option.importedBy}` : ''
              return (
                <Flex vertical={true} title={`${id}\n${date}\n${email}`}>
                  {option?.id && <div>{id}</div>}

                  <div>{date}</div>

                  {option?.importedBy && (
                    <div className='imported-by'>{email}</div>
                  )}
                </Flex>
              )
            }}
            formatLabel={(option) => {
              if (!option?.id) {
                return <EllipsisTooltip text={option?.label} />
              }
              return (
                <EllipsisTooltip text={`id: ${option.id} :: ${option.label}`} />
              )
            }}
          />

          <DropdownActions
            fileType={fileType}
            downloadUrl={`${downloadUrl}/${fileName}`}
            uploadState={uploadState}
            reloadData={reloadData}
          />
        </div>
      )}
    </>
  )
}

FileActions.propTypes = {
  filesList: PropTypes.array,
  formRef: PropTypes.object.isRequired,
  fileType: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  is_uploading: PropTypes.bool.isRequired,
  reloadData: PropTypes.func.isRequired,
}

export default FileActions
